/**
 * React Query hooks for the Array credit-report approval workflow + writeback.
 * See ./service.ts for the underlying Gobodo mutations / queries.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keycloakService } from '../keycloakService';
import { useAbilityContext } from '../../contexts/AbilityContext';
import { useEffect } from 'react';
import {
  ARRAY_ADMIN_FALLBACK_ROLES,
  ARRAY_APPROVER_GOBODO_ROLES,
  ARRAY_APPROVER_ROLE,
  ArrayCreditReportRecord,
  ArrayRequestStatus,
  CreateRequestInput,
  backfillStandardFields,
  createArrayRequest,
  ensureClientEdge,
  listAllPendingApprovals,
  listForClient,
  patchArrayRequest,
} from './service';

const QUERY_KEY = ['arrayCreditReport'] as const;

/** Resolve the current user's identity for requestedBy / approvedBy columns. */
export function useCurrentUserId(): string {
  const info = keycloakService.getUserInfo();
  return info?.email || info?.id || 'unknown';
}

/**
 * Does the current user have permission to approve Array credit-pull requests?
 *
 * Per the "How to pull 3B Credit Report" ops doc, approvers "are basically
 * credit block team leads" — so we match on the resolved Gobodo role name
 * (`Credit Block Team Lead`, `System Administrator`) rather than just
 * Keycloak realm roles. Keycloak roles stay as a secondary fallback.
 */
export function useIsArrayApprover(): boolean {
  const { roles } = useAbilityContext();
  const roleNames = (roles ?? []).map((r) => r.name);
  const hasApproverGobodoRole = roleNames.some((name) => ARRAY_APPROVER_GOBODO_ROLES.includes(name));
  const hasKeycloakFallback =
    keycloakService.hasRole(ARRAY_APPROVER_ROLE) ||
    keycloakService.hasAnyRole(ARRAY_ADMIN_FALLBACK_ROLES);
  return hasApproverGobodoRole || hasKeycloakFallback;
}

/**
 * Every Array credit-report record currently in 'pending_approval' state,
 * across all clients. Powers the F3 approver inbox. Only enabled when the
 * current user is an approver — non-approvers should never trigger this
 * query (they have nothing to act on and it scans every CR record).
 */
export function useAllPendingArrayApprovals(enabled = true) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'pending-approvals'] as const,
    queryFn: listAllPendingApprovals,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** All Array credit-report records for this client, newest first. */
export function useArrayCreditReports(clientId: string, arrayClientKey?: string, enabled = true) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'list', clientId, arrayClientKey ?? null],
    queryFn: () => listForClient(clientId, arrayClientKey),
    enabled: enabled && !!clientId,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The "current" request for the given client+key: prefer any in-flight
 * (pending_approval or approved-but-not-completed) record; fall back to the
 * most recent completed record so the UI can show the latest Array metadata.
 *
 * Side effect: when a record is loaded, fire-and-forget ensure the
 * Client → CreditReport graph edge exists. Idempotent (no-op if the edge
 * is already there). This backfills records that were created before
 * the graph-edge code shipped, so they start appearing in the existing
 * Credit Reports list traversal.
 */
export function useCurrentArrayRequest(
  clientId: string,
  arrayClientKey?: string,
  enabled = true,
): ArrayCreditReportRecord | null {
  const { data } = useArrayCreditReports(clientId, arrayClientKey, enabled);
  const sorted = data ? [...data].sort((a, b) => (b.requestedAt ?? '').localeCompare(a.requestedAt ?? '')) : [];
  const inFlight = sorted.find(
    (r) => r.requestStatus === 'pending_approval' || r.requestStatus === 'approved',
  );
  const current = inFlight ?? sorted[0] ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!current?.key || !current?.clientId) return;
    let cancelled = false;
    (async () => {
      // 1. Ensure the Client → CR edge exists (records pre-dating today's
      //    code don't have it).
      try {
        await ensureClientEdge({ key: current.key, clientId: current.clientId });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[arrayCreditReport] ensureClientEdge failed:', err);
      }
      if (cancelled) return;

      // 2. Backfill standard CR fields (Bureau, Pull Date, Product Code,
      //    Status, Name, Total Files Attached) on records that don't
      //    have them yet. No-op if already backfilled.
      let didBackfill = false;
      try {
        didBackfill = await backfillStandardFields(current);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[arrayCreditReport] backfillStandardFields failed:', err);
      }
      if (cancelled) return;

      // 3. Invalidate the credit-reports list query so the row picks up
      //    the freshly-written edge + standard fields. Always invalidate
      //    on first ensure run (cheap, ensures we don't show stale data).
      queryClient.invalidateQueries({ queryKey: ['credit-report-records', current.clientId] });
      if (didBackfill) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [current?.key, current?.clientId, queryClient]);

  return current;
}

export function useSubmitArrayRequest() {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUserId();
  const isApprover = useIsArrayApprover();

  return useMutation({
    mutationFn: async (input: Omit<CreateRequestInput, 'requestedBy' | 'approvedBy'>) => {
      return createArrayRequest({
        ...input,
        requestedBy: currentUser,
        approvedBy: isApprover ? currentUser : undefined,
      });
    },
    onSuccess: (_record, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, 'list', variables.clientId],
      });
    },
  });
}

export function useApproveArrayRequest() {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUserId();

  return useMutation({
    mutationFn: async (recordId: string) => {
      const now = new Date().toISOString();
      return patchArrayRequest(
        recordId,
        {
          requestStatus: 'approved',
          approvedBy: currentUser,
          approvedAt: now,
        },
        {
          appendHistory: {
            id: `evt-${now}-approved`,
            action: 'approved',
            approvedBy: currentUser,
            approvalDate: now,
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRejectArrayRequest() {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUserId();

  return useMutation({
    mutationFn: async ({ recordId, reason }: { recordId: string; reason: string }) => {
      const now = new Date().toISOString();
      return patchArrayRequest(
        recordId,
        {
          requestStatus: 'rejected',
          approvedBy: currentUser,
          approvedAt: now,
          rejectionReason: reason,
        },
        {
          appendHistory: {
            id: `evt-${now}-rejected`,
            action: 'rejected',
            approvedBy: currentUser,
            approvalDate: now,
            notes: reason,
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRecallArrayRequest() {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUserId();
  return useMutation({
    mutationFn: async (recordId: string) => {
      const now = new Date().toISOString();
      return patchArrayRequest(
        recordId,
        { requestStatus: 'recalled' },
        {
          appendHistory: {
            id: `evt-${now}-recalled`,
            action: 'recalled',
            approvedBy: currentUser,
            approvalDate: now,
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/** Persist Array vendor metadata + file IDs onto the approved record. */
export function usePersistArrayMetadata() {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUserId();
  return useMutation({
    mutationFn: async (input: {
      recordId: string;
      arrayOrderId: string;
      arrayReportKey?: string;
      arrayDisplayToken?: string;
      arrayFirstOrderedAt?: string;
      arrayPdfStatus?: string;
      arrayJsonStatus?: string;
      arrayPdfFileId?: string;
      arrayJsonFileId?: string;
      complete?: boolean;
    }) => {
      const { recordId, complete, ...updates } = input;
      const now = new Date().toISOString();
      return patchArrayRequest(
        recordId,
        {
          ...updates,
          ...(complete ? { requestStatus: 'completed' as ArrayRequestStatus } : {}),
        },
        complete
          ? {
              appendHistory: {
                id: `evt-${now}-completed`,
                action: 'pull_completed',
                approvedBy: currentUser,
                approvalDate: now,
                notes: `orderId=${input.arrayOrderId ?? ''}`,
              },
            }
          : undefined,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
